import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import CtraderConnection from '@/pages/home/components/CtraderConnection';
import TelegramAccounts from '@/pages/home/components/TelegramAccounts';

const SUPER_ADMIN_EMAIL = 'admin@chin.vn';

interface ConnectedAccount {
  id: number;
  user_email: string;
  type: 'ctrader' | 'mt4' | 'telegram';
  field1: string;
  field2: string;
  added_by: 'user_request' | 'admin_manual';
  created_at: string;
}

export default function Home() {
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState<ConnectedAccount[]>([]);

  // MT4 add form state
  const [mt4Login, setMt4Login] = useState('');
  const [mt4Server, setMt4Server] = useState('');
  const [mt4Saving, setMt4Saving] = useState(false);
  const [mt4Msg, setMt4Msg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // MT4 multi-entry edit state
  const [mt4EditingId, setMt4EditingId] = useState<number | null>(null);
  const [mt4EditLogin, setMt4EditLogin] = useState('');
  const [mt4EditServer, setMt4EditServer] = useState('');
  const [mt4EditSaving, setMt4EditSaving] = useState(false);
  const [mt4DeleteTarget, setMt4DeleteTarget] = useState<number | null>(null);

  // MT4 accounts for the super admin (from user_connections)
  const mt4MasterAccounts = connected.filter((c) => c.type === 'mt4' && c.user_email === SUPER_ADMIN_EMAIL);

  const loadData = async () => {
    try {
      const { data: connsResult } = await supabase
        .from('user_connections')
        .select('*')
        .order('created_at', { ascending: false });
      setConnected(connsResult || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session?.user) {
        setUserEmail((session.user.email || '').toLowerCase().trim());
      }
      loadData().finally(() => {
        if (!cancelled) setLoading(false);
      });
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddMt4 = async (e: React.FormEvent) => {
    e.preventDefault();
    setMt4Msg(null);

    if (!mt4Login.trim() || !mt4Server.trim()) {
      setMt4Msg({ type: 'error', text: 'Please fill in both MT4 Login and Server' });
      return;
    }

    setMt4Saving(true);
    try {
      const { data: inserted, error: insertErr } = await supabase
        .from('user_connections')
        .insert({
          user_email: userEmail,
          type: 'mt4',
          field1: mt4Login.trim(),
          field2: mt4Server.trim(),
          added_by: 'admin_manual',
        })
        .select()
        .single();

      if (insertErr) throw insertErr;

      if (inserted) {
        setConnected((prev) => [inserted as ConnectedAccount, ...prev]);
      }

      setMt4Login('');
      setMt4Server('');
      setMt4Msg({ type: 'success', text: `MT4 account "${mt4Login.trim()}" added successfully!` });
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as Record<string, unknown>).message)
        : 'Failed to add MT4 account';
      setMt4Msg({ type: 'error', text: msg });
    } finally {
      setMt4Saving(false);
    }
  };

  const handleUpdateMt4 = async (id: number) => {
    if (!mt4EditLogin.trim() || !mt4EditServer.trim()) {
      setMt4Msg({ type: 'error', text: 'Please fill in both fields' });
      return;
    }
    setMt4EditSaving(true);
    setMt4Msg(null);
    try {
      const { error: updateErr } = await supabase
        .from('user_connections')
        .update({ field1: mt4EditLogin.trim(), field2: mt4EditServer.trim() })
        .eq('id', id);

      if (updateErr) throw updateErr;

      setConnected((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, field1: mt4EditLogin.trim(), field2: mt4EditServer.trim() }
            : c
        )
      );

      setMt4EditingId(null);
      setMt4Msg({ type: 'success', text: 'MT4 account updated!' });
      setTimeout(() => setMt4Msg(null), 3000);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as Record<string, unknown>).message)
        : 'Failed to update MT4 account';
      setMt4Msg({ type: 'error', text: msg });
    } finally {
      setMt4EditSaving(false);
    }
  };

  const handleDeleteMt4 = async (id: number) => {
    try {
      const { error: delErr } = await supabase
        .from('user_connections')
        .delete()
        .eq('id', id);

      if (delErr) throw delErr;

      setConnected((prev) => prev.filter((c) => c.id !== id));
      setMt4DeleteTarget(null);
      setMt4EditingId(null);
      setMt4Msg({ type: 'success', text: 'MT4 account deleted.' });
      setTimeout(() => setMt4Msg(null), 3000);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as Record<string, unknown>).message)
        : 'Failed to delete MT4 account';
      setMt4Msg({ type: 'error', text: msg });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-foreground-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground-950">Dashboard</h1>
          <p className="text-foreground-600 text-sm mt-0.5">
            Super Admin — manage accounts & signals{' — '}
            <span className="text-foreground-800 font-medium">{userEmail}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></div>
            <span className="text-primary-400 text-xs font-medium">Live</span>
          </div>
        </div>
      </div>

      <CtraderConnection />

      <div>
        <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Telegram Accounts</h2>
        <p className="text-foreground-500 text-xs">
          Quản lý nhiều tài khoản Telegram nhận thông báo. Mỗi tài khoản có thể nhận một hoặc nhiều loại tín hiệu — hoặc tất cả.
        </p>
      </div>

      <TelegramAccounts />

      <div>
        <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-1">MT4 Master Account Configuration</h2>
        <p className="text-foreground-500 text-xs">
          Add MT4 accounts that will receive copy trade signals from your cTrader (Spotware) accounts. These MT4 accounts will appear in the Copy Targets sidebar and Copy Trade Engine.
        </p>
      </div>

      <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-bar-chart-grouped-line text-accent-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">MT4 Master Accounts</h3>
            <p className="text-foreground-500 text-xs mt-0.5">
              {mt4MasterAccounts.length > 0
                ? `${mt4MasterAccounts.length} account${mt4MasterAccounts.length > 1 ? 's' : ''} configured`
                : 'No accounts yet — add your first MT4 account below'}
            </p>
          </div>
        </div>

        {/* Existing MT4 Accounts List */}
        {mt4MasterAccounts.length > 0 && (
          <div className="space-y-2 mb-5">
            {mt4MasterAccounts.map((acc) => {
              const isEditing = mt4EditingId === acc.id;
              const isDeleting = mt4DeleteTarget === acc.id;
              return (
                <div
                  key={acc.id}
                  className={`bg-background-50 rounded-lg border transition-all ${
                    isEditing
                      ? 'border-accent-500/50 ring-1 ring-accent-500/20'
                      : 'border-background-200/70 hover:border-background-300/60'
                  }`}
                >
                  {isEditing ? (
                    <div className="p-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-medium text-foreground-500 mb-1">MT4 Login</label>
                          <input
                            type="text"
                            value={mt4EditLogin}
                            onChange={(e) => setMt4EditLogin(e.target.value)}
                            className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-md text-xs text-foreground-900 focus:outline-none focus:border-accent-500/50 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-foreground-500 mb-1">Server</label>
                          <input
                            type="text"
                            value={mt4EditServer}
                            onChange={(e) => setMt4EditServer(e.target.value)}
                            className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-md text-xs text-foreground-900 focus:outline-none focus:border-accent-500/50 transition-all"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleUpdateMt4(acc.id)}
                          disabled={mt4EditSaving}
                          className="px-3 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {mt4EditSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setMt4EditingId(null); setMt4Msg(null); }}
                          className="px-3 py-1.5 bg-background-200/70 hover:bg-background-300/60 text-foreground-600 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : isDeleting ? (
                    <div className="p-3 flex items-center justify-between">
                      <span className="text-red-400 text-xs font-medium">Delete this MT4 account?</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleDeleteMt4(acc.id)}
                          className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-background-50 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => { setMt4DeleteTarget(null); setMt4Msg(null); }}
                          className="px-3 py-1.5 bg-background-200/70 hover:bg-background-300/60 text-foreground-600 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 flex items-center justify-between group">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-md bg-accent-500/10 flex items-center justify-center shrink-0">
                          <i className="ri-signal-tower-line text-accent-400 text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-foreground-900 text-sm font-medium truncate">{acc.field1}</p>
                          <p className="text-foreground-500 text-xs">{acc.field2}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => { setMt4EditingId(acc.id); setMt4EditLogin(acc.field1); setMt4EditServer(acc.field2); setMt4Msg(null); }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-400 hover:bg-accent-500/10 transition-all cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-xs"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => setMt4DeleteTarget(acc.id)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add New MT4 Account Form */}
        <div className="border-t border-background-200/70 pt-4">
          <h4 className="text-xs font-heading font-semibold text-foreground-800 mb-3">
            {mt4MasterAccounts.length === 0 ? 'Add Your First MT4 Account' : 'Add Another MT4 Account'}
          </h4>

          <form onSubmit={handleAddMt4}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">MT4 Login</label>
                <input
                  type="text"
                  value={mt4Login}
                  onChange={(e) => { setMt4Login(e.target.value); setMt4Msg(null); }}
                  placeholder="e.g. 12345678"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-all"
                />
                <p className="text-foreground-400 text-[11px] mt-1">Your MT4 account login number</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Server</label>
                <input
                  type="text"
                  value={mt4Server}
                  onChange={(e) => { setMt4Server(e.target.value); setMt4Msg(null); }}
                  placeholder="e.g. ICMarkets-Demo01"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-all"
                />
                <p className="text-foreground-400 text-[11px] mt-1">MT4 broker server name</p>
              </div>
            </div>

            {mt4Msg && (
              <div className={`mt-4 rounded-lg px-3 py-2 text-xs flex items-start gap-2 ${
                mt4Msg.type === 'success'
                  ? 'bg-primary-500/10 border border-primary-500/20 text-primary-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                <i className={`${mt4Msg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-sm shrink-0 mt-px`}></i>
                <span>{mt4Msg.text}</span>
              </div>
            )}

            <div className="mt-4">
              <button
                type="submit"
                disabled={mt4Saving}
                className="px-5 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {mt4Saving ? 'Saving...' : 'Add MT4 Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}